import { Document, Page, Text } from '@react-pdf/renderer';
import type { GetLightInvoicesResponse } from '@weft/api-definition';
import { isNullOrUndefined, type ItemOf } from '@weft/utils';
import React from 'react';
import { CommitmentProductLineItem } from './light-invoice-statement-document-components/CommitmentProductLineItem/CommitmentProductLineItem';
import { FixedProductLineItem } from './light-invoice-statement-document-components/FixedProductLineItem';
import { Header } from './light-invoice-statement-document-components/Header';
import { Notes } from './light-invoice-statement-document-components/Notes';
import { PromotionLineItem } from './light-invoice-statement-document-components/PromotionLineItem';
import { RevisionedProductLineItem } from './light-invoice-statement-document-components/RevisionedProductLineItem';
import { Subtotal } from './light-invoice-statement-document-components/Subtotal';
import { UsageProductLineItem } from './light-invoice-statement-document-components/UsageProductLineItem';

type LightInvoiceStatementDocumentProps = {
  invoice: ItemOf<GetLightInvoicesResponse['invoices']>;
  accountName: string;
};

export const LightInvoiceStatementDocument: React.FC<LightInvoiceStatementDocumentProps> = ({ invoice, accountName }) => {
  const { commitmentLineItems, fixedLineItems, usageLineItems, promotionLineItems, revisionedLineItems } = invoice;

  return (
    <Document>
      <Page
        size="A4"
        style={{
          padding: '12px 26px',
          fontFamily: 'Inter',
        }}
      >
        <Header
          accountName={accountName}
          billingCycle={invoice.billingCycle}
          invoiceId={invoice.id}
          customerName={invoice.customer.name}
        />

        {commitmentLineItems.map(commitmentLineItem => (
          <CommitmentProductLineItem
            key={crypto.randomUUID()}
            commitmentLineItem={commitmentLineItem}
            currency={invoice.currency}
          />
        ))}

        {fixedLineItems.map(fixedLineItem => (
          <FixedProductLineItem
            key={fixedLineItem.productId}
            fixedLineItem={fixedLineItem}
            currency={invoice.currency}
          />
        ))}

        {usageLineItems.map(usageLineItem => (
          <UsageProductLineItem
            key={crypto.randomUUID()}
            usageLineItem={usageLineItem}
            currency={invoice.currency}
          />
        ))}

        {promotionLineItems.map(promotionLineItem => (
          <PromotionLineItem
            key={promotionLineItem.id}
            lineItemPromotion={promotionLineItem.lineItemPromotion}
            currency={invoice.currency}
            chargedAmount={invoice.revenueBreakdown.total}
            promotionsAmount={invoice.revenueBreakdown.discount}
          />
        ))}

        {revisionedLineItems.map(revisionedLineItem => isNullOrUndefined(revisionedLineItem.baseLineItemId) && (
          <RevisionedProductLineItem
            key={revisionedLineItem.id}
            revisionedLineItem={revisionedLineItem}
            currency={invoice.currency}
          />
        ))}

        <Subtotal
          total={invoice.revenueBreakdown.total}
          currency={invoice.currency}
        />

        <Notes invoiceNotes={invoice.notes}/>

        <Text
          fixed
          render={({ pageNumber, totalPages }) => (
            `- ${pageNumber} / ${totalPages} -`
          )}
          style={{
            marginHorizontal: 'auto',
            marginTop: '12px',
            fontSize: '10px',
          }}
        />
      </Page>
    </Document>
  );
};